import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowRight, ShoppingCart, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/lib/cart';
import { eur } from '@/lib/format';
import { PublicHeader, Footer } from '@/components/Header';

export default function CartPage() {
  const items = useCart(s => s.items);
  const remove = useCart(s => s.remove);
  const updateQty = useCart(s => s.updateQty);
  const subtotal = useCart(s => s.subtotal());
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-10 w-full">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Carrello</div>
          <h1 className="font-display text-3xl font-bold">Il tuo ordine</h1>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl bg-white border border-[--brand-border] p-10 text-center">
            <ShoppingCart size={40} className="mx-auto text-[--brand-muted]" />
            <p className="mt-3 text-[--brand-muted]">Il carrello è vuoto</p>
            <Link to="/menu"><Button className="mt-4 rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]">Vai al menu</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(it => (
              <div key={it.id} className="rounded-2xl bg-white border border-[--brand-border] p-4 shadow-[var(--shadow-sm)]" data-testid="cart-item">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="font-display font-bold text-lg">{it.name}</div>
                    {it.components_summary?.length > 0 && (
                      <ul className="text-xs text-[--brand-muted] mt-1 space-y-0.5">
                        {it.components_summary.map((c, i) => <li key={i}>• {c}</li>)}
                      </ul>
                    )}
                    {it.notes && <div className="mt-1 text-xs italic text-[--brand-muted]">Note: {it.notes}</div>}
                  </div>
                  <div className="text-right">
                    <div className="tabular-nums font-semibold">{eur(it.unit_price * it.quantity)}</div>
                    <div className="text-xs text-[--brand-muted]">{eur(it.unit_price)} cad.</div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[--brand-border] gap-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Button variant="secondary" size="icon" className="rounded-lg h-8 w-8" onClick={() => updateQty(it.id, it.quantity - 1)}><Minus size={14}/></Button>
                    <div className="w-8 text-center tabular-nums font-semibold">{it.quantity}</div>
                    <Button variant="secondary" size="icon" className="rounded-lg h-8 w-8" onClick={() => updateQty(it.id, it.quantity + 1)}><Plus size={14}/></Button>
                  </div>
                  <div className="flex items-center gap-1 ml-auto">
                    {(it.kind === 'custom' || it.kind === 'combo') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (it.kind === 'combo' && it.combo_id) {
                            nav(`/builder?combo=${it.combo_id}&edit=${it.id}`);
                          } else {
                            nav(`/builder?edit=${it.id}`);
                          }
                        }}
                        className="text-[--brand-primary]"
                        data-testid="cart-edit-item"
                      >
                        <Pencil size={14} className="mr-1"/> Modifica
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-[--brand-danger]" data-testid="cart-remove-item"><Trash2 size={14} className="mr-1"/> Rimuovi</Button>
                  </div>
                </div>
              </div>
            ))}

            <div className="rounded-2xl bg-white border border-[--brand-border] p-5 shadow-[var(--shadow-sm)]">
              <div className="flex items-center justify-between">
                <span className="text-[--brand-muted]">Subtotale (senza consegna)</span>
                <span className="tabular-nums font-semibold" data-testid="cart-subtotal">{eur(subtotal)}</span>
              </div>
              <p className="text-xs text-[--brand-muted] mt-2">La commissione di consegna verrà calcolata al checkout in base al tuo indirizzo.</p>
              <Button className="w-full mt-4 rounded-xl bg-[--brand-accent] hover:bg-[--brand-accent-hover]"
                      onClick={() => nav('/checkout')}
                      data-testid="cart-checkout-button">
                Vai al checkout <ArrowRight size={16} className="ml-2"/>
              </Button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
